import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./MonthCalendar.css";

const MonthCalendar = () => {
  const { t, i18n } = useTranslation();

  // ⬇️ "today" sans l'heure pour des comparaisons fiables
  const today = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);

  // Mois affiché dans l'en-tête + grille
  const [currentDate, setCurrentDate] = useState(today);
  // Date sélectionnée par l'utilisateur (par défaut: aujourd'hui)
  const [selectedDate, setSelectedDate] = useState(today);

  // ✅ Mois traduits
  const months = [
    t('monthCalendar.months.january'),
    t('monthCalendar.months.february'),
    t('monthCalendar.months.march'),
    t('monthCalendar.months.april'),
    t('monthCalendar.months.may'),
    t('monthCalendar.months.june'),
    t('monthCalendar.months.july'),
    t('monthCalendar.months.august'),
    t('monthCalendar.months.september'),
    t('monthCalendar.months.october'),
    t('monthCalendar.months.november'),
    t('monthCalendar.months.december'),
  ];

  // ✅ Jours de la semaine traduits (version courte)
  const daysOfWeek = [
    t('monthCalendar.days.sunday.short'),
    t('monthCalendar.days.monday.short'),
    t('monthCalendar.days.tuesday.short'),
    t('monthCalendar.days.wednesday.short'),
    t('monthCalendar.days.thursday.short'),
    t('monthCalendar.days.friday.short'),
    t('monthCalendar.days.saturday.short'),
  ];

  // ✅ Jours de la semaine (version longue pour aria-label)
  const daysOfWeekLong = [
    t('monthCalendar.days.sunday.long'),
    t('monthCalendar.days.monday.long'),
    t('monthCalendar.days.tuesday.long'),
    t('monthCalendar.days.wednesday.long'),
    t('monthCalendar.days.thursday.long'),
    t('monthCalendar.days.friday.long'),
    t('monthCalendar.days.saturday.long'),
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Cases vides avant le 1er du mois
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    // Tous les jours du mois
    for (let day = 1; day <= daysInMonth; day++) days.push(day);
    return days;
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1
    ));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1
    ));
  };

  const handleDateClick = (day) => {
    if (!day) return;
    const viewYear = currentDate.getFullYear();
    const viewMonth = currentDate.getMonth();
    const newSelected = new Date(viewYear, viewMonth, day);
    setSelectedDate(newSelected);
    setCurrentDate(newSelected);
  };

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const isSelectedDate = (day) => {
    if (!day) return false;
    const viewYear = currentDate.getFullYear();
    const viewMonth = currentDate.getMonth();
    const cellDate = new Date(viewYear, viewMonth, day);
    return isSameDay(cellDate, selectedDate);
  };

  // ✅ Formater la date pour aria-label
  const formatDateAriaLabel = (day) => {
    if (!day) return undefined;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dayOfWeek = daysOfWeekLong[date.getDay()];
    const monthName = months[date.getMonth()];
    const year = date.getFullYear();
    
    // Format: "Monday, January 27, 2026"
    return t('monthCalendar.dateFormat', {
      dayOfWeek,
      day,
      month: monthName,
      year,
    });
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div 
      className="month-calendar"
      role="region"
      aria-label={t('monthCalendar.ariaLabel')}
    >
      {/* Header with month/year and navigation */}
      <div className="month-calendar__header">
        <button 
          className="month-calendar__nav-btn month-calendar__nav-btn--prev" 
          onClick={handlePrevMonth} 
          aria-label={t('monthCalendar.previousMonth')}
          title={t('monthCalendar.previousMonth')}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16"
            viewBox="0 0 16 16" 
            fill="none"
            aria-hidden="true"
          >
            <path d="M10.1831 4.175L6.35811 8L10.1831 11.825L8.99977 13L3.99977 8L8.99977 3L10.1831 4.175Z" fill="#B5BEC6"/>
          </svg>
        </button>

        <h3 className="month-calendar__title">
          {months[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>

        <button 
          className="month-calendar__nav-btn month-calendar__nav-btn--next" 
          onClick={handleNextMonth} 
          aria-label={t('monthCalendar.nextMonth')}
          title={t('monthCalendar.nextMonth')}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16"
            viewBox="0 0 16 16" 
            fill="none"
            aria-hidden="true"
          >
            <path d="M5 11.825L8.825 8L5 4.175L6.18333 3L11.1833 8L6.18333 13L5 11.825Z" fill="#B5BEC6"/>
          </svg>
        </button>
      </div>

      {/* Days of week header */}
      <div className="month-calendar__weekdays">
        {daysOfWeek.map((day, index) => (
          <div 
            key={day} 
            className="month-calendar__weekday"
            aria-label={daysOfWeekLong[index]}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="month-calendar__grid" role="grid">
        {days.map((day, index) => (
          <div
            key={index}
            className={`month-calendar__day ${day ? "month-calendar__day--clickable" : "month-calendar__day--empty"} ${isSelectedDate(day) ? "month-calendar__day--selected" : ""}`}
            onClick={() => handleDateClick(day)}
            role={day ? "gridcell" : undefined}
            tabIndex={day ? 0 : -1}
            onKeyDown={(e) => {
              if (day && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleDateClick(day);
              }
            }}
            aria-current={day && isSelectedDate(day) ? "date" : undefined}
            aria-label={formatDateAriaLabel(day)}
            aria-selected={day && isSelectedDate(day) ? "true" : undefined}
          >
            {day}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthCalendar;